import { useState } from "react";

import cn from "classnames";

import { UnGuard } from "constants/interfaces";
import { GetTripsResponse } from "lib/tripsApiTypes";
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import getFormattedDate from "utils/getFormattedDate";

import { IMG, Button, Heading, ChevronDown, Stack, Text, HR } from "components";
import { pushToDataLayer } from "utils/gtm";
import { useTranslation } from "react-i18next";

const ActivitiesDetails = ({
  activities,
}: {
  activities: GetTripsResponse["activities"];
}) => {
  const [isExpanded, setExpanded] = useState(() => {
    return Object.keys(activities ?? {}).reduce<Record<string, boolean>>(
      (acc, day) => {
        acc[day] = true;
        return acc;
      },
      {},
    );
  });

  return (
    <Stack>
      {Object.entries(activities ?? {}).map(([dayKey, dayActivities]) => (
        <Stack key={dayKey} className="mb-10 gap-y-5">
          <Stack className="items-start -bg--theme-colors-surface-neutral-level03 -rounded-heading-container mb-5 md:mb-0">
            <Button
              className="min-h-10"
              variant={"ghost-unstyled"}
              onClick={() => {
                const eventName = isExpanded[dayKey]
                  ? "my_activities_collapse"
                  : "my_activities_expand";
                pushToDataLayer({
                  event: eventName,
                  screen_name: "dashboard",
                  journey_step: "activities",
                });
                setExpanded((prev) => ({
                  ...prev,
                  [dayKey]: !prev[dayKey],
                }));
              }}
            >
              <div className="flex items-center gap-x-[5px]">
                <div className="p-2">
                  <ChevronDown
                    className={cn(
                      isExpanded[dayKey] ? "" : "rotate-180",
                      "-text--theme-colors-action-ghost-default-fg grow",
                    )}
                    height={20}
                    width={20}
                  />
                </div>

                <Text className="font-bold leading-6 -text--theme-colors-text-subtitle-brand text-base">
                  {getFormattedDate(dayKey)}
                </Text>
              </div>
            </Button>
          </Stack>

          {isExpanded[dayKey] &&
            dayActivities?.map((activity, i) => {
              return (
                <div key={i}>
                  {i > 0 && <HR className="my-0" />}
                  <Activity activity={activity} number={i + 1} />
                </div>
              );
            })}
        </Stack>
      ))}
    </Stack>
  );
};

const Activity = ({
  activity,
  number,
}: {
  activity: UnGuard<UnGuard<GetTripsResponse["activities"]>[string]>[number];
  number: number;
}) => {
  const { t } = useTranslation();
  const EVENT_ASSETS = useAppAssetsWithSrc();
  return (
    <Stack className="gap-y-2.5 overflow-hidden">
      <Heading
        as="h4"
        isBold={false}
        className="-text--theme-colors-text-subtitle-brand text-sm"
      >
        {t("activitiesSection.activityLabel")} {number}
      </Heading>

      <Stack className="md:flex-row items-start md:items-center md:gap-x-8">
        <IMG
          className="max-md:hidden h-[170px] w-[300px] object-cover rounded-sg"
          src={activity.cover_image || EVENT_ASSETS.eventPlaceholder.src}
          fallbackSrc={EVENT_ASSETS.eventPlaceholder.fallbackSrc}
          alt="hotel-img"
        />

        <Stack className="flex-1 -text--theme-colors-text-body">
          <Stack className="gap-y-5 text-sm">
            <Stack className="gap-y-5 md:flex-row">
              <ActivityDetail
                className="md:flex-1"
                title={t("activitiesSection.titleLabel")}
                value={activity.name}
              />

              <ActivityDetail
                className="md:flex-[2]"
                title={t("activitiesSection.locationLabel")}
                value={activity.address}
              />
            </Stack>

            <Stack className="gap-y-5 md:flex-row">
              <ActivityDetail
                className="md:flex-1"
                title={t("activitiesSection.dateLabel")}
                value={getFormattedDate(activity.activity_date)}
              />

              <ActivityDetail
                className="md:flex-[2]"
                title={t("activitiesSection.timeLabel")}
                value={`${activity.from} - ${activity.till}`}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ActivityDetail = ({
  title,
  value,
  className,
}: {
  title: string;
  value?: string;
  className: string;
}) => {
  return (
    <div className={cn("flex flex-col gap-y-1 min-w-0", className)}>
      <Heading
        as="h4"
        isBold={false}
        className="-text--theme-colors-text-subtitle-brand text-sm"
      >
        {title}
      </Heading>

      <Text className="line-clamp-4 pe-1">{value}</Text>
    </div>
  );
};

export default ActivitiesDetails;
